import { AlertTriangle } from "lucide-react";
import i18n from "@/lib/i18n/init";

interface NoScriptFallbackProps {
  /** Routed locale segment, used to pick the message language. */
  locale: string;
  /** Absolute URL of the current page, or the path when the host is unknown. */
  url: string;
}

/**
 * Rendered inside the document shell, above the app tree, so it lands in the
 * very first SSR flush. The app itself is client-rendered (I18nProvider holds
 * children back until an effect runs), so when scripts cannot execute the page
 * is otherwise completely blank.
 *
 * This happens whenever the page is framed with a `sandbox` attribute that
 * omits `allow-scripts`. Mail clients and in-app browsers that render a message
 * body in a sandboxed frame propagate those flags to anything opened from it,
 * including `target="_blank"` popups when `allow-popups-to-escape-sandbox` is
 * absent.
 *
 * The link carries no `target`: a popup would be blocked outright by such a
 * sandbox, while a plain anchor stays long-pressable ("open in browser" /
 * "copy link") and the visible URL stays selectable.
 */
export function NoScriptFallback({ locale, url }: NoScriptFallbackProps) {
  const t = i18n.getFixedT(locale);

  return (
    <noscript>
      <div className="grid min-h-dvh place-items-center p-6">
        <div className="w-full max-w-xl rounded-xl border border-black/5 bg-white/80 shadow-md">
          <div className="flex flex-col items-center gap-5 p-6 text-center sm:p-8">
            <span className="inline-flex size-12 items-center justify-center rounded-full bg-amber-100 text-amber-700">
              <AlertTriangle className="size-6" aria-hidden="true" />
            </span>

            <div className="space-y-2">
              <h1 className="text-2xl font-semibold tracking-tight text-neutral-900">
                {t("NoScript.title")}
              </h1>
              <p className="text-sm text-neutral-600">{t("NoScript.message")}</p>
            </div>

            <div className="w-full space-y-2 pt-2">
              <p className="text-xs font-medium tracking-wide text-neutral-500 uppercase">
                {t("NoScript.open_label")}
              </p>
              <a
                href={url}
                className="block rounded-lg bg-neutral-100 px-4 py-3 text-sm break-all text-neutral-900 underline"
              >
                {url}
              </a>
            </div>
          </div>
        </div>
      </div>
    </noscript>
  );
}
